// Faixa de acompanhamento da importação de clientes do ERP.
//
// A importação roda num processo do servidor e pode levar de segundos a
// dezenas de minutos. Ela é global: enquanto uma está rodando, ninguém mais
// inicia outra. Por isso esta faixa aparece para QUALQUER usuário com a tela
// aberta, não só para quem começou — é o que explica o botão desabilitado.
//
// É faixa e não modal de propósito: quem não iniciou a importação continua
// podendo navegar e filtrar a tela enquanto ela corre.
import { Alert, Box, LinearProgress, Typography } from "@mui/material";

// Enquanto o servidor não publica a primeira medida, a barra fica
// indeterminada — melhor do que mostrar 0% e parecer travada.
function BarraProgresso({ valor }) {
  const indeterminada = valor === null || valor === undefined;

  return (
    <LinearProgress
      variant={indeterminada ? "indeterminate" : "determinate"}
      value={indeterminada ? undefined : Math.min(100, Math.max(0, valor))}
      sx={{ height: 6, borderRadius: 3, mt: 0.75 }}
    />
  );
}

export default function ProgressoImportacao({ estado, sx }) {
  if (!estado) return null;

  const numero = (valor) => Number(valor || 0).toLocaleString("pt-BR");

  // Desfechos: mostrados por pouco tempo, depois que a fila já foi liberada.
  if (estado.status === "CONCLUIDO" || estado.status === "FALHA") {
    const falhou = estado.status === "FALHA";
    return (
      <Box sx={sx}>
        <Alert severity={falhou ? "error" : "success"} variant="outlined">
          {falhou
            ? `Importação interrompida após ${numero(estado.importados)} cliente(s). ${
                estado.erro || ""
              } Os que já entraram permanecem no sistema — repetir continua de onde parou.`
            : `Importação concluída: ${numero(estado.importados)} cliente(s) adicionados.`}
        </Alert>
      </Box>
    );
  }

  // Um contador só, do começo ao fim: clientes prontos (criados e
  // configurados) sobre o total. Ele nunca recua. O andamento da fase atual fica
  // no rótulo da etapa — outro lugar na tela, para os dois números não serem
  // lidos como se fossem o mesmo.
  const concluidos = Number(estado.concluidos || 0);
  const total = Number(estado.total || 0);

  return (
    <Box sx={sx}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {estado.etapa || "Importando clientes"}
          {estado.etapaDetalhe ? ` (${estado.etapaDetalhe})` : ""}
          {estado.usuarioNome ? ` — iniciada por ${estado.usuarioNome}` : ""}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {numero(concluidos)} de {numero(total)} prontos
          {estado.progresso !== null && estado.progresso !== undefined
            ? ` · ${estado.progresso}%`
            : ""}
        </Typography>
      </Box>

      <BarraProgresso valor={estado.progresso} />

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 0.5 }}
      >
        Enquanto a importação estiver em andamento, não é possível iniciar
        outra. Pode fechar a tela — ela continua rodando no servidor.
      </Typography>
    </Box>
  );
}
